import { isTemplateDependencyFunctionValidationError } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useIsErrorForField } from '../../hooks/useIsErrorForField'
import {
  isConfigurationFunction,
  isSubTemplatedField,
  isSubTemplatedFieldValue,
  isTemplateField,
  type SubTemplateFieldValue,
  type TemplateField,
  type TemplateVersion,
  ValueSetupRole,
} from '../../types/templateModel'
import { isInherited } from '../../uiModels/templateOperations/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import {
  type AddDynamicFunctionPlaceholder,
  isAddDynamicFunctionPlaceholder,
} from '../dynamicFunction/types'
import { getSpecialCasedSubTemplatedFieldValue } from '../specialCasedSubTemplatedViewers/helpers'
import {
  isSpecialCasedSubTemplatedValueSetup,
  type SpecialCasedSubTemplatedValueSetup,
} from '../specialCasedSubTemplatedViewers/types'
import { useSpecialSubTemplatedTemplatedConfigurations } from '../specialCasedSubTemplatedViewers/useSpecialSubTemplatedTemplatedConfigurations'
import type { StackableType } from '../stackableTable/types'
import {
  type ConfigurationTemplateFunctionValueWithPosition,
  isTemplateVersionValidationError,
  SIMPLIFIED_TEMPLATE_FIELDS_COLUMN_RATIOS,
  TEMPLATE_FIELDS_COLUMN_RATIOS,
} from './fieldsHelpers'
import { functionIdentifier } from './functionHelpers'
import { useIsErrorForSpecialCasedSubTemplatedFieldValue } from './useIsErrorForSpecialCasedSubTemplatedFieldValue'

const CONFIGURATION_FUNCTION_COLUMN_RATIOS = [1] as const

type StackableValue =
  | ConfigurationTemplateFunctionValueWithPosition
  | TemplateField
  | TemplateVersion
  | SubTemplateFieldValue
  | SpecialCasedSubTemplatedValueSetup
  | AddDynamicFunctionPlaceholder

const isFieldActive = <F extends StackableValue>(
  detailsPath: Optional<string>,
  field: F,
) => {
  if (isAddDynamicFunctionPlaceholder(field)) {
    return false
  } else if (isSpecialCasedSubTemplatedValueSetup(field)) {
    return (
      !!detailsPath &&
      detailsPath === getSpecialCasedSubTemplatedFieldValue(field)?.path
    )
  } else if (isConfigurationFunction(field)) {
    return !!detailsPath && detailsPath === field.path
  } else if (isTemplateField(field)) {
    return !!detailsPath && field.path === detailsPath
  } else if (isSubTemplatedFieldValue(field)) {
    return !!detailsPath && field.path === detailsPath
  } else {
    // TemplateVersion
    return !!detailsPath && field.path === detailsPath
  }
}

type CreateStackableTypeInput<D extends StackableValue> = {
  data: D
  headersCount: number
  isHeaderElement: boolean
  specialCasedType: Optional<string>
  isSimplifiedView: boolean
  activePath: Optional<string>
}

export const useConvertToStackableType = () => {
  const { errors } = useOperationsContext()
  const { templateVersion } = useTemplateContext()
  const isErrorForField = useIsErrorForField()
  const isErrorForSpecialCasedSubTemplatedFieldValue =
    useIsErrorForSpecialCasedSubTemplatedFieldValue()
  const specialCaseConfigurations =
    useSpecialSubTemplatedTemplatedConfigurations()

  assertNotEmpty(templateVersion)

  return useCallback(
    function createStackableType<D extends StackableValue>({
      data,
      specialCasedType,
      isHeaderElement,
      isSimplifiedView,
      headersCount,
      activePath,
    }: CreateStackableTypeInput<D>): StackableType<D> {
      const isActive = isFieldActive(activePath, data)

      if (isAddDynamicFunctionPlaceholder(data)) {
        return {
          value: data,
          columns: 2,
          key: () => `${data.parent.path}-addDynamicFunction`,
          ratios: CONFIGURATION_FUNCTION_COLUMN_RATIOS,
          isEmpty: true,
          errors: [],
          isActive,
          shouldShowOnOverflow: false,
        }
      }

      const numberOfWideColumnsForFunctions = isSimplifiedView ? 2 : 3
      const functionFieldColumns =
        headersCount - numberOfWideColumnsForFunctions

      let ratios = isSimplifiedView
        ? SIMPLIFIED_TEMPLATE_FIELDS_COLUMN_RATIOS
        : TEMPLATE_FIELDS_COLUMN_RATIOS

      const specialCaseRatios = specialCasedType
        ? specialCaseConfigurations[
            specialCasedType
          ]?.variableWidthTableColumns?.map(({ ratio }) => ratio)
        : undefined

      if (specialCasedType && specialCaseRatios) {
        ratios = specialCaseRatios
      }

      // Only applicable to special cased fields
      if (isSpecialCasedSubTemplatedValueSetup(data)) {
        return {
          value: data,
          columns: headersCount,
          key: () => data.subTemplateIdentifier,
          ratios,
          isEmpty: false,
          errors: errors.filter(({ error }) =>
            isErrorForSpecialCasedSubTemplatedFieldValue(data, error),
          ),
          isActive,
          shouldShowOnOverflow: isSimplifiedView,
          isInherited: isInherited(data.field),
        }
      }

      if (isConfigurationFunction(data)) {
        return {
          value: data,
          columns: functionFieldColumns,
          key: (index) => `${data.functionIdentifier}-${index}`,
          ratios: CONFIGURATION_FUNCTION_COLUMN_RATIOS,
          isEmpty: !data.function?.fplusSource && !data.documentation?.value,
          errors: isHeaderElement
            ? []
            : errors.filter(
                ({ error }) =>
                  isTemplateDependencyFunctionValidationError(error) &&
                  ((data.type === 'dynamic' && error.isDynamic) ||
                    (data.type === 'declared' && !error.isDynamic)) &&
                  (error.source
                    ? data.function?.fplusSource === error.source
                    : data.functionIdentifier ===
                      functionIdentifier({ source: undefined })),
              ),
          isActive,
          shouldShowOnOverflow: isSimplifiedView,
          isInherited: isInherited(data),
        }
      }

      if (isTemplateField(data)) {
        return {
          value: data,
          columns: headersCount,
          key: () => data?.path,
          ratios,
          isEmpty:
            !data.value ||
            (isTemplateField(data) &&
              isSubTemplatedField(data) &&
              data.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
              !!data?.isRenderingEmpty),
          errors: errors.filter(({ error }) => isErrorForField(data, error)),
          isActive,
          shouldShowOnOverflow: isSimplifiedView,
          isInherited: isInherited(data),
        }
      }

      if (isSubTemplatedFieldValue(data)) {
        return {
          value: data,
          columns: headersCount,
          key: () => data?.path,
          ratios,
          isEmpty: false,
          errors: errors.filter(({ error }) => isErrorForField(data, error)),
          isActive,
          shouldShowOnOverflow: isSimplifiedView,
          isInherited: isInherited(data),
        }
      }

      // TemplateVersion
      return {
        columns: headersCount,
        key: () => data.versionInfo.identifier,
        ratios,
        value: data,
        errors: errors.filter(({ error }) =>
          isTemplateVersionValidationError(error, data.versionInfo),
        ),
        isActive,
        shouldShowOnOverflow: isSimplifiedView,
      }
    },
    [
      errors,
      isErrorForField,
      isErrorForSpecialCasedSubTemplatedFieldValue,
      specialCaseConfigurations,
    ],
  )
}
